import { useState, useCallback, useEffect } from "react";

import type { LensSDKException, BusinessSubscription } from "lens-platform-sdk";
import { Button, Modal } from "@k8slens/lds";
import { FormInput } from "@k8slens/lds-form";

import { useAnalytics } from "src/hooks/useAnalytics";

import Notification from "src/components/Notification/Notification";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";

import styles from "./AssignLensAppIQModal.module.css";

interface Props {
  open: boolean;
  assigningOrgId: boolean;
  assignOrgIdError?: LensSDKException | null;
  disabled: boolean;
  onModalClose: () => void;
  subscription: BusinessSubscription;
  initialOrgId: string | null;
  onClickAssign: (subscription: BusinessSubscription, orgId: string) => Promise<void>;
}

const cancelButtonLabel = "Cancel";
const assignButtonLabel = "Assign";
const unassignButtonLabel = "Unassign";
const title = "Assign Organization ID";

const AssignLensAppsModal = ({
  open,
  assigningOrgId,
  assignOrgIdError,
  disabled,
  onModalClose,
  subscription,
  initialOrgId,
  onClickAssign,
}: Props) => {
  const { trackButtonClicked } = useAnalytics();

  const [orgId, setOrgId] = useState<string>(initialOrgId || "");

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setOrgId(initialOrgId || "");
  }, [initialOrgId]);

  const handleSubmit = async (orgId: string) => {
    trackButtonClicked(assignButtonLabel);
    await onClickAssign(subscription, orgId);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleAssign = () => {
    trackButtonClicked(assignButtonLabel);
    handleSubmit(orgId);
  };

  const handleUnassign = () => {
    trackButtonClicked(unassignButtonLabel);
    setOrgId("");
    handleSubmit("");
  };

  return (
    <Modal
      title={title}
      size="md"
      className={styles.modalContent}
      onClose={() => {
        if (!assigningOrgId) {
          onModalClose();
          reset();
        }
      }}
      isOpen={open}
      footer={
        <>
          <button
            aria-label={cancelButtonLabel}
            type="button"
            onClick={() => {
              if (!assigningOrgId) {
                trackButtonClicked(cancelButtonLabel);
                onModalClose();
                reset();
              }
            }}
            className="lds-button"
          >
            {cancelButtonLabel}
          </button>
          {initialOrgId && !orgId ? (
            <Button
              loading={assigningOrgId}
              label={unassignButtonLabel}
              id="unAssignOrgIdButton"
              buttonType="submit"
              type="danger"
              onClick={handleUnassign}
              disabled={disabled}
            />
          ) : (
            <Button
              loading={assigningOrgId}
              label={assignButtonLabel}
              id="assignOrgIdButton"
              buttonType="submit"
              primary
              onClick={handleAssign}
              disabled={disabled || !orgId || orgId === initialOrgId}
            />
          )}
        </>
      }
    >
      <form aria-label={title} autoComplete="off">
        {assignOrgIdError && assignOrgIdError !== null ? (
          <Notification
            level="error"
            message={assignOrgIdError?.message ?? "unknown"}
            type="flash"
            className={styles.errorNotification}
          />
        ) : null}
        <aside className={styles.description}>
          <h5>How to find the Organization Id?</h5>
          <p>
            Sign in to{" "}
            <TrackedAnchor href="https://apps.lenscloud.io/" target="_blank" className="primary">
              Lens AppIQ
            </TrackedAnchor>
            . Open "Settings" and choose "Subscription" from the side navigation. Copy the Organization ID and paste it
            here.
          </p>
          <img alt="How to find the Organization Id" src="/static/media/lens-apps-org-id.png" width="387" />
        </aside>
        <fieldset>
          <FormInput
            id="orgId"
            name="orgId"
            label="Organization ID"
            value={orgId}
            onChange={setOrgId}
            autoFocus={true}
          />
        </fieldset>
      </form>
    </Modal>
  );
};

export default AssignLensAppsModal;
